/**
 * The words of an offer to add an exercise (PC-I75): every kind that offers
 * to write an entry next to what a template already holds. The stale
 * routine's (#274), the lagging region's (#273) and a plateau's variation
 * (#272) differ in their line, which is the backend's, in code's words; the
 * controls are the same, drawn by `OfferControls` from `offerView`.
 *
 * Pure, so the rules are tested rather than judged on a screen. The iOS twin
 * is GroundworkKit's `AddOfferPresentation`, and the words both render come
 * from the shared deck (`COACH_COPY` / `CoachCopy`), which
 * `scripts/check_copy_decks.py` keeps byte-identical.
 *
 * A kind supplies only its decline. Everything else is shared: the pill
 * "Add to {template}", one scope line that promises only what `add_rows`
 * guarantees, and the two record lines, chosen by the answer the server
 * recorded.
 */
import type { OpenerOffer } from '../../api/types'
import { COACH_COPY } from '../../copy/coach'
import { fill } from '../../copy/firstRun'
import type { OfferView } from './offerPresentation'

/**
 * The add kinds this renderer draws, and each one's decline (PC-I75). A
 * decline records itself and folds the line away, so there is no record line
 * and no Undo after a no. A kind is declared to the server (`answers=`, see
 * `OPENER_OFFER_KINDS`) only in the change that registers its backend.
 */
export const ADD_OFFER_DECLINES: Readonly<Record<string, string>> = {
  stale_add: COACH_COPY.addOfferKeepRoutine,
  region_add: COACH_COPY.addOfferNotNow,
  variation: COACH_COPY.addOfferHoldingThis,
}

/** Whether this renderer draws an offer of this kind. */
export function isAddOffer(kind: string | null | undefined): boolean {
  return kind != null && Object.hasOwn(ADD_OFFER_DECLINES, kind)
}

/**
 * The template's name in an add offer's detail, or null. The name is the
 * user's own text, so it is only ever put into a string, never read as
 * anything else.
 */
export function templateName(offer: OpenerOffer): string | null {
  const name = offer.detail?.template
  return typeof name === 'string' && name.trim().length > 0 ? name : null
}

/**
 * Which controls an add offer gets (PC-I75, PC-I72).
 *
 * Drawn only while it can be answered: unanswered, with `can_change` true.
 * After a yes the record line says what happened, from the answer the server
 * recorded ("accepted": written; "already_there": the template already named
 * it), so "Added" is never claimed for a write that did not happen (IS-I39).
 * A yes is final, so there is no Undo; a decline folds the line away, so
 * there is no record line. An offer whose template cannot be named draws
 * nothing: a button that cannot say where it writes is not offered.
 */
export function addOfferView(offer: OpenerOffer | null | undefined): OfferView {
  if (!offer || !isAddOffer(offer.kind)) return { kind: 'none' }
  const template = templateName(offer)
  if (template === null) return { kind: 'none' }
  if (offer.answer === 'accepted') {
    return { kind: 'answered', record: fill(COACH_COPY.addOfferAdded, { template }), undo: false }
  }
  if (offer.answer === 'already_there') {
    return { kind: 'answered', record: fill(COACH_COPY.addOfferAlreadyThere, { template }), undo: false }
  }
  if (offer.answer != null || offer.can_change === false) return { kind: 'none' }
  return {
    kind: 'offer',
    accept: fill(COACH_COPY.addOfferAccept, { template }),
    decline: ADD_OFFER_DECLINES[offer.kind],
    scope: COACH_COPY.addOfferScope,
  }
}
